import type { MessageSignerWalletAdapterProps, SignerWalletAdapterProps } from '@solana/wallet-adapter-base'

export enum AuthStep {
  TERMS = 1,
  WALLETS = 2,
  WALLET_CONNECT = 3,
  WALLET_CONNECTED = 4,
  WALLET_CONFIRMED = 5,
  TOTP = 6,
  PASSKEY = 7,
}

export enum Identities {
  SOLANA_ACCOUNT = 'SOLANA_ACCOUNT',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  TOTP = 'TOTP',
  PASSKEY = 'PASSKEY',
}

export enum SignType {
  MESSAGE = 'MESSAGE',
  TRANSACTION = 'TRANSACTION',
}

export enum ResponseTypes {
  VerifyPublicKeyWithMfa = 'VerifyPublicKeyWithMfa',
  VerifyPublicKeyResponse = 'VerifyPublicKeyResponse',
  VerifyPublicKeyForEmbeddedBrowser = 'VerifyPublicKeyForEmbeddedBrowser',
  SWPO_WITH_SECOND_FACTOR = 'SWPO_WITH_SECOND_FACTOR',
  WITH_SIGNING = 'WITH_SIGNING',
  SWPO_WITHOUT_SECOND_FACTOR = 'SWPO_WITHOUT_SECOND_FACTOR',
  SETUP_TOTP_IMMEDIATELY = 'SETUP_TOTP_IMMEDIATELY',
  SPECIAL_CASE = 'SPECIAL_CASE',
}

export interface VerifyPublicKey {
  dataForSign: string;
  publicKey: string;
  signTx: Function;
  userId: string;
}

export interface LinkPublicKey {
  challenge: {
    dataType: SignType;
    rawData: string;
  };
}

export interface VerifyPublicKeyMessage extends Omit<VerifyPublicKey, 'signTx'> {
  signMessage: MessageSignerWalletAdapterProps['signMessage'] | undefined;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface VerifyPublicKeyResponseWithoutMfa {
  accessToken: string;
  refreshToken: string;
  type: ResponseTypes.VerifyPublicKeyResponse;
}

export interface VerifyPublicKeyResponseWithMfa {
  requiredSecondFactor: Identities.TOTP | Identities.PASSKEY;
  type: ResponseTypes.VerifyPublicKeyWithMfa | ResponseTypes.VerifyPublicKeyForEmbeddedBrowser;
}

export type VerifyPublicKeyResponse = VerifyPublicKeyResponseWithoutMfa | VerifyPublicKeyResponseWithMfa

export interface SigninResponseWithSigning {
  challenge: {
    dataType: SignType;
    rawData: string;
  };
  publicKey: string;
  type: ResponseTypes.WITH_SIGNING;
  userId: string;
}

export interface SignUpResponse {
  challenge: {
    dataType: SignType;
    rawData: string;
  };
  userId: string;
}

export interface SigninResponseWithoutWalletSignAndSecondFactor extends AuthTokens{
  type: ResponseTypes.SWPO_WITHOUT_SECOND_FACTOR;
}

export interface SigninResponseWithoutWalletSigning {
  requiredSecondFactor: Identities.TOTP | Identities.PASSKEY;
  type: ResponseTypes.SWPO_WITH_SECOND_FACTOR;
  userId: string;
}

export interface SigninFromDapp {
  type: ResponseTypes.VerifyPublicKeyForEmbeddedBrowser;
  userId: string;
}

export interface SingInFromDappWithImmediateTOTP {
  type: ResponseTypes.SETUP_TOTP_IMMEDIATELY;
  userId: string;
}

export interface VerifyEmailForEmbeddedBrowser {
  availableSecondFactors: (Identities.TOTP | Identities.PASSKEY)[];
  type: ResponseTypes.SWPO_WITH_SECOND_FACTOR;
  userId: string;
}

export interface VerifyEmailSpecialResponse {
  accessToken: string;
  refreshToken: string;
  type: ResponseTypes.SPECIAL_CASE;
}

export interface VerifyEmailWithSeconfFactor extends SigninResponseWithoutWalletSigning {}

export type SignInResponses = SigninResponseWithSigning | SigninResponseWithoutWalletSigning | SigninResponseWithoutWalletSignAndSecondFactor | SigninFromDapp | SingInFromDappWithImmediateTOTP
export type VerifyEmailResponses = VerifyEmailSpecialResponse | VerifyEmailForEmbeddedBrowser | VerifyEmailWithSeconfFactor

export interface Identity {
  type: Identities;
  value: string;
}

export interface UserProfile {
  address: {
    city: string;
    country: string;
    flat: string;
    house: string;
    postCode: string;
    state: string;
    street: string;
    subStreet: string;
    typeDescriptor: 'ADDRESS';
    userId: string;
  };
  birthDate: string;
  createdAt: string;
  firstName: string;
  identityItems: Identity[];
  kycStatus: 'NOT_STARTED' | 'APPROVED' | 'PENDING' | 'REJECTED' | 'STARTED';
  lastName: string;
  passkeys: [];
  preferredSecondFactor: string;
  profileImageUrl: string;
  userId: string;
  refCodeIsApplied: boolean;
  referralCode: string;
}

export interface SignInWithPK {
  confirmType: SignType;
  publicKey: string;
  signMessage: MessageSignerWalletAdapterProps['signMessage'];
  signTx: SignerWalletAdapterProps['signTransaction'];
}

export interface SignUpWithPK extends SignInWithPK {}
